// frontend/src/services/api.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    if (error.response?.status === 403) {
      const data = error.response?.data;
      if (data?.action === 'upgrade_package') {
        import('react-toastify').then(({ toast }) => {
          toast.error(
            <div>
              <strong>Limit Exceeded!</strong>
              <p>{data.message || 'Please upgrade your package.'}</p>
              <a href="/packages" style={{ color: '#fff', textDecoration: 'underline' }}>Upgrade Now</a>
            </div>,
            { autoClose: false }
          );
        });
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/register', data),
  login: (data) => api.post('/login', data),
  logout: () => api.post('/logout'),
  me: () => api.get('/me'),
  checkLicense: (data) => api.post('/check-license', data),
  verifyEmail: (data) => api.post('/email/verify', data),
  forgotPassword: (data) => api.post('/forgot-password', data),
  resetPassword: (data) => api.post('/reset-password', data),
};

// Package endpoints
export const packageAPI = {
  getAll: () => api.get('/packages'),
  getById: (id) => api.get(`/packages/${id}`),
};

// Subscription endpoints
export const subscriptionAPI = {
  purchasePackage: (packageId) => api.post('/subscriptions/purchase', { package_id: packageId }),
  getCurrentSubscription: () => api.get('/subscriptions/current'),
  cancelSubscription: () => api.post('/subscriptions/cancel'),
};

// Team Member endpoints
export const teamMemberAPI = {
  getAll: () => api.get('/team-members'),
  create: (data) => api.post('/team-members', data),
  update: (id, data) => api.put(`/team-members/${id}`, data),
  delete: (id) => api.delete(`/team-members/${id}`),
  toggleStatus: (id) => api.post(`/team-members/${id}/toggle-status`),
};

// Project endpoints
export const projectAPI = {
  // getAll: () => api.get('/projects'),
  // getAll: (page = 1) => api.get(`/projects?page=${page}`),
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/projects${query ? `?${query}` : ''}`);
  },
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      return api.post(`/projects/${id}`, data);
    }
    return api.put(`/projects/${id}`, data);
  },
  delete: (id) => api.delete(`/projects/${id}`),
  getUpdatedSince: (timestamp) => api.get(`/projects/updated-since/${timestamp}`),

  getBurndown: (projectId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/projects/${projectId}/burndown${query ? `?${query}` : ''}`);
  },

  getSprintBurndown: (projectId, sprintStart, sprintEnd) => {
    return api.get(`/projects/${projectId}/sprint-burndown`, {
      params: { sprint_start: sprintStart, sprint_end: sprintEnd }
    });
  },
};

// Task endpoints
export const taskAPI = {
  // getAll: () => api.get('/tasks'),
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/tasks${query ? `?${query}` : ''}`);
  },
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      return api.post(`/tasks/${id}`, data);
    }
    return api.put(`/tasks/${id}`, data);
  },
  delete: (id) => api.delete(`/tasks/${id}`),
  getUpdatedSince: (timestamp) => api.get(`/tasks/updated-since/${timestamp}`),
};

// Comment endpoints
export const commentAPI = {
  getByTask: (taskId) => api.get(`/tasks/${taskId}/comments`),
  create: (taskId, data) => {
    const config = {
      headers: {
        'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
      },
    };
    return api.post(`/tasks/${taskId}/comments`, data, config);
  },
  update: (taskId, commentId, data) => api.put(`/tasks/${taskId}/comments/${commentId}`, data),
  delete: (taskId, commentId) => api.delete(`/tasks/${taskId}/comments/${commentId}`),
  getByProject: (projectId) => api.get(`/projects/${projectId}/comments`),
  createForProject: (projectId, data) => {
    const config = {
      headers: {
        'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
      },
    };
    return api.post(`/projects/${projectId}/comments`, data, config);
  },
  update: (commentId, data) => api.put(`/comments/${commentId}`, data),
  delete: (commentId) => api.delete(`/comments/${commentId}`),
  getUpdatedSince: (timestamp, params = {}) => {
    const query = new URLSearchParams({ ...params, timestamp }).toString();
    return api.get(`/comments/updated-since?${query}`);
  },
};

// Attachment endpoints
export const attachmentAPI = {
  upload: (data, taskId) => {
    const config = {
      headers: { 'Content-Type': 'multipart/form-data' },
    };
    data.append('task_id', taskId);
    return api.post('/attachments', data, config);
  },
  listByTask: (taskId) => api.get(`/attachments?task_id=${taskId}`),
  listByProject: (projectId) => api.get(`/attachments?project_id=${projectId}`),
  delete: (attachmentId) => api.delete(`/attachments/${attachmentId}`),
};

// Time Tracking endpoints
export const timeEntryAPI = {
  start: (data) => api.post('/time/start', data),
  pause: (id) => api.post(`/time/pause/${id}`),
  resume: (id) => api.post(`/time/resume/${id}`),
  stop: (id) => api.post(`/time/stop/${id}`),
  show: (id) => api.get(`/time/entry/${id}`),
  manual: (data) => api.post('/time/entries', data),
  listByTask: (taskId) => api.get(`/time/entries?task_id=${taskId}`),
};

// Report & Calendar endpoints
export const reportAPI = {
  getDashboardStats: () => api.get('/reports/dashboard'),
  getBillingStats: () => api.get('/reports/billing-stats'),
  getCalendarEvents: () => api.get('/calendar/events'),
  getBillingSummary: () => api.get('/reports/billing-summary'),
  getOverdueTasks: () => api.get('/reports/overdue-tasks'),
};

// User endpoints ✅ KPI endpoints included
export const userAPI = {
  create: (data) => api.post('/users', data),
  getAll: () => api.get('/team-members'),
  getPerformance: () => api.get('/users/performance'),
  getTeamPerformance: () => api.get('/users/team-performance'),
  updateProfile: (data) => api.put('/users/profile', data),
  // getTimeTracking: () => api.get('/my-time-tracking'),
  // KPI endpoints
  getKpiReport: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/users/kpi-report${query ? `?${query}` : ''}`);
  },
  getKpis: (status = 'all') => api.get(`/users/kpis?status=${status}`),
  updateKpi: (kpiId, data) => api.put(`/users/kpis/${kpiId}`, data),
  deleteKpi: (kpiId) => api.delete(`/users/kpis/${kpiId}`),
  updateKpiProgress: (kpiId, actual) => api.post(`/users/kpis/${kpiId}/progress`, { actual }),
  updateCompanyProfile: (data) => api.put('/users/company-profile', data),
  getChatUsers: () => api.get('/team-members'),
};

// Role endpoints
export const roleAPI = {
  getAll: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  updatePermissions: (id, data) => api.put(`/roles/${id}/permissions`, data),
};

// Permission endpoints
export const permissionAPI = {
  getAll: () => api.get('/permissions'),
  create: (data) => api.post('/permissions', data),
  update: (id, data) => api.put(`/permissions/${id}`, data),
  delete: (id) => api.delete(`/permissions/${id}`),
};

// Status endpoints
export const statusAPI = {
  getAll: (type = 'project') => api.get(`/statuses?type=${type}`),
  create: (data) => api.post('/statuses', data),
  update: (id, data) => api.put(`/statuses/${id}`, data),
  delete: (id) => api.delete(`/statuses/${id}`),
  toggle: (id) => api.patch(`/statuses/${id}/toggle`),
  reorder: (data) => api.patch(`/statuses/reorder`, data),
};

// Clients endpoints
export const clientAPI = {
  getAll: (params = {}) => api.get('/clients', { params }),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Invoice endpoints
export const invoiceAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/invoices${query ? `?${query}` : ''}`);
  },
  getById: (id) => api.get(`/invoices/${id}`),
  sendEmail: (id) => api.post(`/invoices/${id}/send-email`),
  recordPayment: (id, data) => api.post(`/invoices/${id}/record-payment`, data),
  markAsPaid: (id, data) => api.post(`/invoices/${id}/mark-paid`, data),
  sendEmail: (id) => api.post(`/invoices/${id}/send-email`),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  generateInvoice: (projectId) => api.post(`/projects/${projectId}/generate-invoice`),
};

// Sprint endpoints
export const sprintAPI = {
  // getAll: (projectId) => api.get(`/projects/${projectId}/sprints`),
  getAll: (projectId, params = {}) => {
    const query = new URLSearchParams({
      ...params,
      _t: Date.now()
    }).toString();
    return api.get(`/projects/${projectId}/sprints?${query}`);
  },
  getById: (projectId, sprintId) => api.get(`/projects/${projectId}/sprints/${sprintId}`),
  create: (projectId, data) => api.post(`/projects/${projectId}/sprints`, data),
  update: (projectId, sprintId, data) => api.put(`/projects/${projectId}/sprints/${sprintId}`, data),
  delete: (projectId, sprintId) => api.delete(`/projects/${projectId}/sprints/${sprintId}`),
  start: (projectId, sprintId) => api.post(`/projects/${projectId}/sprints/${sprintId}/start`),
  complete: (projectId, sprintId) => api.post(`/projects/${projectId}/sprints/${sprintId}/complete`),
  addTask: (projectId, sprintId, taskId, storyPoints) =>
    api.post(`/projects/${projectId}/sprints/${sprintId}/tasks`, { task_id: taskId, story_points: storyPoints }),
  removeTask: (projectId, sprintId, taskId) =>
    api.delete(`/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}`),
  getBurndown: (projectId, sprintId) => api.get(`/projects/${projectId}/sprints/${sprintId}/burndown`),
};

// Backlog endpoints
export const backlogAPI = {
  getProjectBacklog: (projectId) => api.get(`/projects/${projectId}/backlog`),
  getOverdueTasks: (projectId) => api.get(`/projects/${projectId}/backlog/overdue`),
  addToBacklog: (projectId, data) => api.post(`/projects/${projectId}/backlog`, data),
  moveToSprint: (projectId, taskId, sprintId, additionalData = {}) =>
    api.post(`/projects/${projectId}/backlog/tasks/${taskId}/move-to-sprint`, {
      sprint_id: sprintId,
      ...additionalData
    }),
  reorder: (projectId, tasks) => api.post(`/projects/${projectId}/backlog/reorder`, { tasks }),
  delete: (projectId, taskId) => api.delete(`/projects/${projectId}/backlog/tasks/${taskId}`),
};


// Conversation endpoints
export const conversationAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/conversations${query ? `?${query}` : ''}`);
  },
  getById: (id) => api.get(`/conversations/${id}`),
  createDirect: (recipientId) => api.post('/conversations/direct', { recipient_id: recipientId }),
  createGroup: (name, userIds) => api.post('/conversations/group', { name, user_ids: userIds }),
  update: (id, data) => api.put(`/conversations/${id}`, data),
  delete: (id) => api.delete(`/conversations/${id}`),
};

// Message endpoints
export const messageAPI = {
  send: (conversationId, data) => {
    const config = {
      headers: {
        'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
      },
    };
    return api.post(`/conversations/${conversationId}/messages`, data, config);
  },
  getById: (id) => api.get(`/messages/${id}`),
  update: (id, data) => api.put(`/messages/${id}`, data),
  delete: (id) => api.delete(`/messages/${id}`),
  downloadAttachment: (id) => 
    api.get(`/messages/${id}/download`, { responseType: 'blob' }),
};

// User online status endpoint
export const userStatusAPI = {
  update: (status) => api.post('/user/status', { status }),
  getOnline: () => api.get('/users/online'),
};

// Chat API (for real-time user list updates)
export const chatAPI = {
  getUsers: () => api.get('/team-members'),
  getOnlineUsers: () => api.get('/users/online'),
};